import { useEffect, useState } from 'react';

import { filtersListAtom } from '@core/Atoms/Plp/FiltersListAtom';
import { aggregationsAtom } from '@core/Atoms/Search/AggregationsAtom';
import { DealershipAttrs } from '@core/Entities/Dealership/Dealership.entity';
import { Locale } from '@core/Entities/Locale/Locale.entity';
import { FilterParams } from '@core/Entities/Search/QueryParams.entity';
import { FilterValueDisplayObj } from '@core/Entities/Search/SearchFilters/FilterValueDisplayObj.entity';
import { getFilterValueDisplayName } from '@core/Utils/SearchFilters/GetFilterValueDisplayName';
import { mapFiltersForDisplay } from '@core/Utils/SearchFilters/MapFiltersForDisplay';
import { getLabelWithEllipsis } from '@core/Utils/Strings/GetLabelWithEllipsis';
import Button from '@gds/Button/Button';
import { Chip } from '@gds/Chip/Chip';
import cn from 'classnames';
import { useAtomValue } from 'jotai';

import stableHash from 'stable-hash';

import { LocationAttrs } from 'Components/SearchWidget/Search.entity';
import { useUpdateFilters } from 'Hooks/SearchFilters/UseUpdateFilters';

import { PlpCombinedFiltersMessages } from '../../FilterPanel.entity';

import { CANT_DELETE, DEFAULT_VISIBLE_NUM } from './ActiveFiltersOverview.constants';

import styles from './ActiveFiltersOverview.module.css';

interface ActiveFiltersOverviewProps {
  locale: Locale;
  strings: PlpCombinedFiltersMessages;
  noBottomBorder?: boolean;
  locationAttrs?: LocationAttrs;
  dealerAttrs?: DealershipAttrs;
}

export const ActiveFiltersOverview = ({
  locale,
  strings,
  noBottomBorder,
  locationAttrs,
  dealerAttrs,
}: ActiveFiltersOverviewProps) => {
  const activeFilters = useAtomValue(filtersListAtom);
  const aggregations = useAtomValue(aggregationsAtom);

  let displayFilters = mapFiltersForDisplay(activeFilters, locale);

  const maxDistanceFilter = displayFilters.find(filter => filter.id === 'max-distance');
  if (locationAttrs || dealerAttrs) {
    displayFilters = maxDistanceFilter
      ? displayFilters.filter(filter => filter.id !== 'max-distance')
      : displayFilters;
  }

  const [willFiltersOverflow, setWillFilterOverflow] = useState(
    displayFilters.length > DEFAULT_VISIBLE_NUM,
  );
  const [isTruncated, setIsTruncated] = useState(true);
  const { updateFilters } = useUpdateFilters();

  const handleRemoveFilter = (filter: FilterValueDisplayObj) => {
    const filterChanges = filter.originalFilters.map(filtertoRemove => {
      if (Array.isArray(filter.originalFilters[0].value)) {
        return {
          id: filtertoRemove.id,
          value: filter.originalFilters[0].value.filter(value => value !== filter.value),
        };
      }

      return { id: filtertoRemove.id, value: undefined };
    });

    updateFilters(filterChanges);
  };

  const handleRemoveLocationFilters = () => {
    updateFilters([
      { id: 'region', value: undefined },
      { id: 'area', value: undefined },
      { id: 'postcode', value: undefined },
      { id: 'lat', value: undefined },
      { id: 'lon', value: undefined },
      { id: 'max-distance', value: undefined },
    ]);
  };

  const handleRemoveMultipleFilters = (filter: FilterValueDisplayObj) => {
    const originalFilter = filter.originalFilters.find(original => original.id === filter.id);

    const remainingValues = (originalFilter?.value as string[]).filter(
      (val: string) => !filter.value?.includes(val),
    );

    const newValues: string[] = remainingValues.length > 0 ? remainingValues : [];

    updateFilters([{ id: filter.id as keyof FilterParams, value: newValues }]);
  };

  const handleRemoveDealershipFilters = () => {
    updateFilters([
      { id: 'region', value: undefined },
      { id: 'area', value: undefined },
      { id: 'postcode', value: undefined },
      { id: 'lat', value: undefined },
      { id: 'lon', value: undefined },
      { id: 'max-distance', value: undefined },
      { id: 'dealer-id', value: undefined },
      { id: 'dealership-id', value: undefined },
    ]);
  };

  const handleRemoveVehicleStatus = () => {
    updateFilters([{ id: 'vehicle-status', value: 'active' }]);
  };

  useEffect(() => {
    setWillFilterOverflow(displayFilters.length > DEFAULT_VISIBLE_NUM);
  }, [stableHash(displayFilters), DEFAULT_VISIBLE_NUM]);

  const displayLength = isTruncated ? DEFAULT_VISIBLE_NUM : displayFilters.length;
  const wrapperClassNames = cn(styles.activeFiltersWrapper, {
    [styles.activeFiltersPresent]: displayFilters.length > 0,
    [styles.noBorder]: noBottomBorder,
  });

  return (
    <div className={wrapperClassNames} role="list">
      {displayFilters.slice(0, displayLength).map(filter => {
        const deleteFn = CANT_DELETE[locale]?.includes(filter.id as keyof FilterParams)
          ? undefined
          : filter.id === 'area' || filter.id === 'region'
          ? () => handleRemoveLocationFilters()
          : filter.id === 'dealership-id'
          ? () => handleRemoveDealershipFilters()
          : filter.id === 'vehicle-status'
          ? () => handleRemoveVehicleStatus()
          : (filter.id === 'finance-product' || filter.id === 'doors') &&
            Array.isArray(filter.value)
          ? () => handleRemoveMultipleFilters(filter)
          : () => handleRemoveFilter(filter);
        const valueId = Array.isArray(filter.value)
          ? filter.value.sort().join('-')
          : `${(typeof filter.value === 'string' ? filter.value : '').replace(/ /g, '')}${(
              filter.valueGte || ''
            ).replace(/ /g, '')}${(filter.valueLte || '').replace(/ /g, '')}`;

        if (filter.id === 'postcode' && !filter.postcode) return null;

        const chipLabel = getFilterValueDisplayName(
          filter,
          strings,
          aggregations,
          locale,
          locationAttrs,
          dealerAttrs,
        );
        return (
          <Chip
            key={`${filter.id}-${valueId}`}
            className={styles.filterChip}
            onDelete={deleteFn}
            variant="filter"
            id={`active-filter-overview-chip-${filter.id}-${valueId}`}
            dataTestId="active-filter-chip"
            role="listitem"
            deleteLabel={strings.overviewRemove}
          >
            {getLabelWithEllipsis(chipLabel!, 30, 27)}
          </Chip>
        );
      })}{' '}
      {willFiltersOverflow && (
        <Button
          className={styles.moreLessBtn}
          variant="link"
          size="small"
          dataTestId="toggle-all-active-filters"
          onClick={() => setIsTruncated(!isTruncated)}
        >
          {isTruncated
            ? `+${displayFilters.length - DEFAULT_VISIBLE_NUM} ${strings.overviewMore}`
            : strings.overviewLess}
        </Button>
      )}
    </div>
  );
};
